import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Shield, AlertTriangle, CheckCircle, Loader2, X, 
  ChevronDown, ChevronUp, BookOpen, AlertCircle, Info
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { API } from "../../lib/api";

const CanonLockModal = ({ 
  open, 
  onClose, 
  projectId,
  chapters = [],
  currentChapter = 1,
  storyBible = {}
}) => {
  const [mode, setMode] = useState("chapter"); // chapter, book
  const [selectedChapter, setSelectedChapter] = useState(currentChapter);
  
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  
  const [bookJob, setBookJob] = useState(null);
  const [expandedIssues, setExpandedIssues] = useState({});

  useEffect(() => {
    if (open) {
      setSelectedChapter(currentChapter);
      setCheckResult(null);
      setBookJob(null);
    }
  }, [open, currentChapter]);

  const getChapterContent = (chapterNum) => {
    const chapter = chapters.find(c => c.chapter_number === chapterNum);
    return chapter?.content || "";
  };

  const buildStoryBiblePayload = () => {
    return {
      characters: storyBible.characters || [],
      world_rules: storyBible.world_rules || storyBible.worldRules || [],
      timeline: storyBible.timeline || [],
      plot_points: storyBible.plot_points || storyBible.plotPoints || []
    };
  };

  const buildChapterSummaries = () => {
    return chapters
      .filter(c => c.chapter_number < selectedChapter && c.content)
      .map(c => ({
        number: c.chapter_number,
        title: c.title,
        summary: c.summary || c.content?.slice(0, 300)
      }));
  };

  // Check single chapter
  const handleCheckChapter = async () => {
    const content = getChapterContent(selectedChapter);
    if (!content || content.length < 100) {
      toast.error("Chapter content too short to check");
      return;
    }

    setChecking(true);
    setCheckResult(null);

    try {
      const res = await axios.post(`${API}/canon-lock/check`, {
        project_id: projectId,
        chapter_number: selectedChapter,
        chapter_content: content,
        story_bible: buildStoryBiblePayload(),
        chapter_summaries: buildChapterSummaries()
      });
      setCheckResult(res.data);
      
      if (res.data.canon_status === "clean") {
        toast.success("No canon violations found!");
      } else if (res.data.canon_status === "violation") {
        toast.error(`${res.data.issue_count.critical} critical violation(s) found!`);
      } else {
        toast.warning(`${res.data.issue_count.total} potential issues found`);
      }
    } catch (error) {
      toast.error("Canon check failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setChecking(false);
    }
  };

  // Check entire book
  const handleCheckBook = async () => {
    const chaptersWithContent = chapters
      .filter(c => c.content && c.content.length > 100)
      .map(c => ({
        chapter_number: c.chapter_number,
        content: c.content
      }));

    if (chaptersWithContent.length === 0) {
      toast.error("No chapters with content to check");
      return;
    }

    setChecking(true);

    try {
      const res = await axios.post(`${API}/canon-lock/check-book/jobs`, {
        project_id: projectId,
        chapters: chaptersWithContent,
        story_bible: buildStoryBiblePayload()
      });

      setBookJob({ id: res.data.job_id, status: "pending", progress: 0 });
      pollBookJob(res.data.job_id);
    } catch (error) {
      setChecking(false);
      toast.error("Failed to start book check: " + (error.response?.data?.detail || error.message));
    }
  };

  const pollBookJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/canon-lock/check-book/jobs/${jobId}`);
      setBookJob(res.data);

      if (res.data.status === "completed") {
        setChecking(false);
        const summary = res.data.summary;
        if (summary.total_issues === 0) {
          toast.success("Book is canon-consistent!");
        } else {
          toast.warning(`Found ${summary.total_issues} issues across ${summary.chapters_with_issues} chapters`);
        }
      } else if (res.data.status === "failed") {
        setChecking(false);
        toast.error("Book check failed: " + res.data.error);
      } else {
        setTimeout(() => pollBookJob(jobId), 3000);
      }
    } catch (error) {
      setChecking(false);
      toast.error("Failed to check job status");
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical": return <AlertCircle className="w-4 h-4 text-red-400" />;
      case "warning": return <AlertTriangle className="w-4 h-4 text-amber-400" />;
      case "minor": return <Info className="w-4 h-4 text-blue-400" />;
      default: return <Info className="w-4 h-4 text-slate-400" />;
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case "critical": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "warning": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "minor": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      default: return "bg-slate-500/20 text-slate-400 border-slate-500/30";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "clean": return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
      case "violation": return "bg-red-500/20 text-red-400 border-red-500/30";
      case "warning": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      default: return "bg-slate-500/20 text-slate-400 border-slate-500/30";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="w-5 h-5 text-violet-400" />
            Canon Lock
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Check your story against the Story Bible for contradictions and inconsistencies
          </DialogDescription>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-3">
          <Button
            variant={mode === "chapter" ? "default" : "ghost"}
            size="sm"
            onClick={() => setMode("chapter")}
            className={mode === "chapter" ? "bg-violet-600 text-white" : "text-slate-400"}
          >
            <Shield className="w-4 h-4 mr-1" />
            Check Chapter
          </Button>
          <Button
            variant={mode === "book" ? "default" : "ghost"}
            size="sm"
            onClick={() => setMode("book")}
            className={mode === "book" ? "bg-violet-600 text-white" : "text-slate-400"}
          >
            <BookOpen className="w-4 h-4 mr-1" />
            Check Entire Book
          </Button>
        </div>

        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {/* Story Bible Status */}
          <div className="bg-slate-800/30 rounded-lg p-3 mb-4 border border-slate-700/50">
            <p className="text-sm text-slate-300 mb-2">Story Bible Status:</p>
            <div className="flex flex-wrap gap-2 text-xs">
              <Badge className="bg-slate-700">
                {storyBible.characters?.length || 0} Characters
              </Badge>
              <Badge className="bg-slate-700">
                {(storyBible.world_rules || storyBible.worldRules)?.length || 0} World Rules
              </Badge>
              <Badge className="bg-slate-700">
                {storyBible.timeline?.length || 0} Timeline Events
              </Badge>
              <Badge className="bg-slate-700">
                {(storyBible.plot_points || storyBible.plotPoints)?.length || 0} Plot Points
              </Badge>
            </div>
          </div>

          {/* CHAPTER MODE */}
          {mode === "chapter" && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <label className="text-sm text-slate-400">Chapter:</label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(parseInt(e.target.value))}
                  className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-white text-sm flex-1"
                >
                  {chapters.map(c => (
                    <option key={c.chapter_number} value={c.chapter_number}>
                      Chapter {c.chapter_number}: {c.title || "Untitled"}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={handleCheckChapter}
                  disabled={checking}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  {checking ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Check Canon</>
                  )}
                </Button>
              </div>

              {/* Chapter Result */}
              {checkResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Status Card */}
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {checkResult.canon_status === "clean" && <CheckCircle className="w-6 h-6 text-emerald-400" />}
                        {checkResult.canon_status === "violation" && <AlertCircle className="w-6 h-6 text-red-400" />}
                        {checkResult.canon_status === "warning" && <AlertTriangle className="w-6 h-6 text-amber-400" />}
                        <span className="text-lg font-medium text-white">
                          {checkResult.canon_status === "clean" ? "Canon Consistent" :
                           checkResult.canon_status === "violation" ? "Canon Violation" : "Potential Issues"}
                        </span>
                      </div>
                      <Badge className={getStatusBadge(checkResult.canon_status)}>
                        {checkResult.issue_count.total} issues
                      </Badge>
                    </div>
                    <div className="flex gap-4 text-xs text-slate-400">
                      <span className="text-red-400">🔴 Critical: {checkResult.issue_count.critical}</span>
                      <span className="text-amber-400">🟡 Warning: {checkResult.issue_count.warning}</span>
                      <span className="text-blue-400">🔵 Minor: {checkResult.issue_count.minor}</span>
                    </div>
                  </div>

                  {/* Issues List */}
                  {checkResult.issues?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-300">Issues Found:</p>
                      {checkResult.issues.map((issue, idx) => (
                        <div
                          key={idx}
                          className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden"
                        >
                          <div
                            className="flex items-start justify-between p-3 cursor-pointer"
                            onClick={() => setExpandedIssues(prev => ({ ...prev, [idx]: !prev[idx] }))}
                          >
                            <div className="flex items-start gap-2">
                              {getSeverityIcon(issue.severity)}
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-[10px] ${getSeverityBadge(issue.severity)}`}>
                                    {issue.severity?.toUpperCase()}
                                  </Badge>
                                  <Badge className="text-[10px] bg-slate-700 text-slate-300">
                                    {issue.category}
                                  </Badge>
                                </div>
                                <p className="text-sm text-white mt-1">{issue.description}</p>
                              </div>
                            </div>
                            {expandedIssues[idx] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                          
                          {expandedIssues[idx] && (
                            <div className="px-3 pb-3 space-y-2 border-t border-slate-700/50 pt-2">
                              <div>
                                <p className="text-xs text-slate-500">From chapter:</p>
                                <p className="text-xs text-cyan-400 italic">"{issue.quote_from_chapter}"</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Contradicts:</p>
                                <p className="text-xs text-amber-400">{issue.contradicts}</p>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500">Suggestion:</p>
                                <p className="text-xs text-emerald-400">{issue.suggestion}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}

          {/* BOOK MODE */}
          {mode === "book" && (
            <div className="space-y-4">
              <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/50">
                <h4 className="font-medium text-white mb-2">Full Book Canon Check</h4>
                <p className="text-sm text-slate-400 mb-4">
                  Scan every chapter against your Story Bible. The check builds context
                  as it goes, so later chapters are checked against earlier ones too.
                </p>
                <p className="text-xs text-slate-500">
                  {chapters.filter(c => c.content && c.content.length > 100).length} chapters will be checked
                </p>
              </div>

              {!bookJob && (
                <Button
                  onClick={handleCheckBook}
                  disabled={checking}
                  className="w-full bg-violet-600 hover:bg-violet-500"
                >
                  {checking ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
                  ) : (
                    <><BookOpen className="w-4 h-4 mr-2" /> Check Entire Book</>
                  )}
                </Button>
              )}

              {bookJob && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-4"
                >
                  {/* Progress */}
                  <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-300">
                        {bookJob.status === "completed" 
                          ? "Check Complete!" 
                          : `Checking Chapter ${bookJob.current_chapter || 0} of ${bookJob.total_chapters || chapters.length}`
                        }
                      </span>
                      <Badge className={
                        bookJob.status === "completed" ? "bg-emerald-500/20 text-emerald-400" :
                        bookJob.status === "failed" ? "bg-red-500/20 text-red-400" :
                        "bg-violet-500/20 text-violet-400"
                      }>
                        {bookJob.status}
                      </Badge>
                    </div>
                    <Progress value={bookJob.progress} className="h-2 bg-slate-700" />
                  </div>

                  {/* Summary */}
                  {bookJob.status === "completed" && bookJob.summary && (
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                      <h4 className="font-medium text-white mb-3">Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400">Total Issues</p>
                          <p className="text-2xl font-bold text-white">{bookJob.summary.total_issues}</p>
                        </div>
                        <div>
                          <p className="text-slate-400">Chapters with Issues</p>
                          <p className="text-2xl font-bold text-amber-400">{bookJob.summary.chapters_with_issues}</p>
                        </div>
                        <div className="col-span-2 flex gap-4 text-xs">
                          <span className="text-red-400">🔴 Critical: {bookJob.summary.critical}</span>
                          <span className="text-amber-400">🟡 Warning: {bookJob.summary.warning}</span>
                          <span className="text-blue-400">🔵 Minor: {bookJob.summary.minor}</span>
                          <span className="text-emerald-400">✅ Clean: {bookJob.summary.clean_chapters}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Chapter Results */}
                  {bookJob.status === "completed" && bookJob.results && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-300">Chapter Results:</p>
                      {bookJob.results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`rounded-lg p-3 border ${
                            result.status === "clean" ? "bg-emerald-950/20 border-emerald-500/30" :
                            result.status === "violation" ? "bg-red-950/20 border-red-500/30" :
                            result.status === "warning" ? "bg-amber-950/20 border-amber-500/30" :
                            "bg-slate-800/30 border-slate-700/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-white">Chapter {result.chapter_number}</span>
                            <Badge className={getStatusBadge(result.status)}>
                              {result.status === "clean" ? "Clean" :
                               result.issue_count ? `${result.issue_count.total} issues` : result.status}
                            </Badge>
                          </div>
                          {result.issues?.length > 0 && (
                            <div className="mt-2 text-xs text-slate-400">
                              {result.issues.slice(0, 2).map((issue, i) => (
                                <p key={i}>• {issue.description}</p>
                              ))}
                              {result.issues.length > 2 && (
                                <p className="text-slate-500">+{result.issues.length - 2} more...</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CanonLockModal;
